import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchPerformanceStepperRequest } from "../../../redux/actions/performanceActions";
import "../PerformanceStyles.css";
import { asArray, stepLabels } from "./performanceUi";
import PerformanceStepper, { PerformanceStepArrows } from "./PerformanceStepper";
import ObjectifsNMoins1Step from "./steps/ObjectifsNMoins1Step";
import CompetencesGeneriquesStep from "./steps/CompetencesGeneriquesStep";
import CompetencesSpecifiquesStep from "./steps/CompetencesSpecifiquesStep";
import PlanDeveloppementStep from "./steps/PlanDeveloppementStep";
import ObjectifsNStep from "./steps/ObjectifsNStep";

const firstOpenStep = (steps) => {
    const nMinusOne = steps.find((step) => step.code === "OBJECTIFS_N_MOINS_1");
    if (nMinusOne) return nMinusOne.code;
    const inProgress = steps.find((step) => step.status === "IN_PROGRESS");
    if (inProgress) return inProgress.code;
    const notStarted = steps.find((step) => step.status === "NOT_STARTED" && !step.blocked);
    if (notStarted) return notStarted.code;
    return steps.find((step) => !step.blocked)?.code || steps[0]?.code;
};

export default function PerformanceEvaluationPage() {
    const { progressId } = useParams();
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const hasProgressId = Boolean(progressId);
    const revisionMode = searchParams.get("mode") === "revision";
    const readOnly = searchParams.get("mode") === "consultation";

    const stepper = useSelector((state) =>
        hasProgressId
            ? state.performance.progress.stepperByProgressId[String(progressId)]
            : null
    );

    const loading = useSelector((state) =>
        hasProgressId
            ? state.performance.progress.stepperLoadingByProgressId[String(progressId)]
            : false
    );

    const error = useSelector((state) =>
        hasProgressId
            ? state.performance.progress.stepperErrorByProgressId[String(progressId)]
            : null
    );

    const steps = useMemo(() => asArray(stepper?.steps), [stepper]);
    const [activeStep, setActiveStep] = useState(null);

    useEffect(() => {
        if (hasProgressId) {
            dispatch(fetchPerformanceStepperRequest(progressId));
        }
    }, [dispatch, progressId, hasProgressId]);

    useEffect(() => {
        if (revisionMode) {
            setActiveStep("OBJECTIFS_N_MOINS_1");
        } else if (hasProgressId && !activeStep && steps.length > 0) {
            setActiveStep(firstOpenStep(steps));
        }
    }, [hasProgressId, activeStep, steps, revisionMode]);

    const renderStep = () => {
        switch (activeStep) {
            case "OBJECTIFS_N_MOINS_1":
                return <ObjectifsNMoins1Step progressId={progressId} revisionMode={revisionMode} readOnly={readOnly} />;
            case "COMPETENCES_GENERIQUES":
                return <CompetencesGeneriquesStep progressId={progressId} />;
            case "COMPETENCES_SPECIFIQUES":
                return <CompetencesSpecifiquesStep progressId={progressId} />;
            case "PLAN_DEVELOPPEMENT":
                return <PlanDeveloppementStep progressId={progressId} />;
            case "OBJECTIFS_N":
                return <ObjectifsNStep progressId={progressId} />;
            default:
                return <div className="perf-state">Selectionnez une etape.</div>;
        }
    };

    if (!hasProgressId) {
        return (
            <main className="perf-page">
                <header className="perf-header">
                    <div>
                        <h1>Evaluation performance</h1>
                        <p>Equipe du manager</p>
                    </div>
                </header>

                <section className="perf-step-panel">
                    <h2>Equipe a evaluer</h2>
                    {/* Mets ici le composant qui affiche l'equipe du manager */}
                </section>
            </main>
        );
    }

    return (
        <main className="perf-page">
            <header className="perf-header">
                <div>
                    <h1>{stepper?.campagneNom || "Évaluation performance"}</h1>
                    <p>{revisionMode ? "Révision des objectifs N-1" : `Progression : ${stepper?.progressionPourcentage ?? 0}%`}</p>
                </div>
            </header>

            {loading && <div className="perf-state">Chargement du stepper...</div>}
            {error && <div className="perf-state perf-state--error">{error}</div>}

            {!loading && !error && steps.length > 0 && (
                <>
                    {!revisionMode && (
                        <PerformanceStepper
                            steps={steps}
                            activeCode={activeStep}
                            onSelect={setActiveStep}
                        />
                    )}
                    <section className="perf-step-panel">
                        <h2>{stepLabels[activeStep] || activeStep}</h2>
                        {renderStep()}
                        {!revisionMode && (
                            <PerformanceStepArrows
                                steps={steps}
                                activeCode={activeStep}
                                onSelect={setActiveStep}
                            />
                        )}
                    </section>
                </>
            )}
        </main>
    );
}
